/** Custom-cursor on/off preference. Persisted in localStorage and reflected as
 *  a `data-custom-cursor` attribute on <html>, which gates the cursor CSS in
 *  src/style.css. Mirrors the direct localStorage pattern used by
 *  stitch-color-picker.tsx — no global settings context exists. Default: on. */

import { CURSOR_REGISTRY } from './cursor-registry';

const STORAGE_KEY = 'stitchpress-custom-cursor';
const ATTR = 'data-custom-cursor';

type Listener = (enabled: boolean) => void;
const listeners = new Set<Listener>();

export function isCustomCursorEnabled(): boolean {
    try {
        // Default on: only an explicit "off" disables it.
        return window.localStorage.getItem(STORAGE_KEY) !== 'off';
    } catch {
        return true;
    }
}

export function setCustomCursorEnabled(enabled: boolean): void {
    try {
        window.localStorage.setItem(STORAGE_KEY, enabled ? 'on' : 'off');
    } catch {
        // Ignore storage failures (private mode, etc.) — still apply visually.
    }
    if (enabled) preloadCursors();
    applyAttr(enabled);
    for (const l of listeners) l(enabled);
}

export function onCustomCursorChange(listener: Listener): () => void {
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
}

/** Apply the current persisted preference to <html>. Call once at startup. */
export function initCustomCursor(): void {
    const enabled = isCustomCursorEnabled();
    if (enabled) preloadCursors();
    applyAttr(enabled);
}

let preloaded = false;

/** Warm the browser cache with every cursor image so the first time a given
 *  cursor is applied it renders immediately. Without this, the browser only
 *  fetches/decodes each cursor PNG the moment it's first used, flashing the
 *  fallback system cursor for a frame — once per cursor kind. `decode()` warms
 *  the decoded-bitmap cache, not just the network cache, which is what the
 *  cursor loader needs. Idempotent. */
export function preloadCursors(): void {
    if (preloaded || typeof window === 'undefined' || typeof Image === 'undefined') return;
    preloaded = true;
    for (const spec of Object.values(CURSOR_REGISTRY)) {
        for (const url of [spec.url, spec.url2x]) {
            const img = new Image();
            img.src = url;
            // Ignore decode failures (e.g. not-yet-loaded) — the network cache
            // is still warmed, which is enough to avoid the flash.
            void img.decode?.().catch(() => {});
        }
    }
}

function applyAttr(enabled: boolean): void {
    if (typeof document === 'undefined') return;
    document.documentElement.setAttribute(ATTR, enabled ? 'on' : 'off');
}
